// Drift: Rykkere på Supabase (del af SupabaseApi – se supabase.ts). Funktionerne i 20261028840000_rykkere.sql tjekker
// rettighed, lov og spærre, regner beløbene igen (de skal stemme med appens) og lægger beskederne i kø.
// Beskedens tekst udfyldes her med virksomhedens egne skabeloner (Beskeder › skabeloner) og den samme beregning som testversionen (core/rykker.ts).
// Inkasso (20261029500000_inkasso.sql): Collectia eller småsag; småsagens PDF-pakke ligger i det private lager "inkasso".
import { ApiFejl, type Api, type RykkerFilter, type RykkerOversigt, type RykkerRaekke } from './api';
import { iDag } from '../../core/dato';
import { udfyld, type Skabelon } from '../../core/automation';
import { TOM_STATUS, erErhverv, hovedstolAf, kanalerFor, normRegler, rykkerVariabler, sendPaa, skabelonFor, tjekRegler, type RenteTjek, type RykkerStatus } from '../../core/rykker';
import { gyldigtSagsnr, rensSagsnr } from '../../core/inkasso';
import type { SbKatalog, SbKontekst } from './sbKatalog';

const LAGER = 'inkasso';

type Raa = Omit<RykkerRaekke, 'hovedstol' | 'status'> & { gebyrer: { type: string; beloeb: number; moms: number }[] | null; status: Partial<RykkerStatus> | null };
type RaaOversigt = Omit<RykkerOversigt, 'raekker' | 'klar' | 'economic'> & { raekker: Raa[]; klar: Raa[] };

const tilRaekke = ({ gebyrer, status, ...x }: Raa): RykkerRaekke => ({
  ...x, kunde: { ...x.kunde, felter: x.kunde.felter ?? {} }, hovedstol: hovedstolAf({ total: x.total, gebyrer }),
  status: { ...TOM_STATUS, ...(status ?? {}), historik: status?.historik ?? [] },
});

export class SbRykker {
  constructor(private readonly c: SbKontekst, private readonly kat: SbKatalog, private readonly egneSkabeloner: () => Promise<Skabelon[]>) {}
  private async rpc<T>(navn: string, args: Record<string, unknown>): Promise<T> {
    return (await this.c.k(this.c.sb.rpc(navn, { k: await this.c.kraevKlient(), ...args }))) as T;
  }
  private async hent(p_booking: number): Promise<Pick<RykkerOversigt, 'raekker' | 'regler' | 'spaerret'>> {
    const o = await this.rpc<RaaOversigt>('rykker_oversigt', { p_booking });
    return { regler: normRegler(o.regler), spaerret: o.spaerret, raekker: o.raekker.map(tilRaekke) };
  }

  /** Én side ad gangen – filtrering, sider og nøgletal regnes i databasen (rykker_liste). */
  rykkerOversigt: Api['rykkerOversigt'] = async (f: RykkerFilter = {}) => {
    const o = await this.rpc<RaaOversigt>('rykker_liste', { p_trin: f.trin ?? null, p_soeg: f.soeg ?? '', p_type: f.type ?? 'alle', p_fra: f.fra ?? 0, p_antal: f.antal ?? 50, p_vis: f.vis ?? null });
    // e-conomic er ikke koblet på endnu: gebyrer og renter ligger på bookingen og kommer med i Betalinger og en senere faktura
    return { ...o, regler: normRegler(o.regler), raekker: o.raekker.map(tilRaekke), klar: o.klar.map(tilRaekke), economic: false };
  };

  sendRykker: Api['sendRykker'] = async (id, niveau) => {
    const [o, indst, egne] = await Promise.all([this.hent(id), this.kat.indstillinger(), this.egneSkabeloner()]);
    if (o.spaerret) throw new ApiFejl('Udsendelse er spærret – rykkere sendes ikke.');
    const x = o.raekker[0];
    if (!x) throw new ApiFejl('Bookingen er ikke en ubetalt faktura.');
    const i = { dato: x.dato, hovedstol: x.hovedstol, erhverv: erErhverv(x.kunde.felter), status: x.status };
    const idag = iDag();
    const nu = sendPaa(i, o.regler, idag);
    if (!nu || nu.niveau !== niveau) throw new ApiFejl('Den besked er allerede sendt. Opdater siden, og prøv igen.');
    const kanaler = kanalerFor(o.regler.kanal, x.kunde);
    if (!kanaler.length) throw new ApiFejl('Kunden har hverken e-mail eller mobil til rykkerens kanal.');
    const sk = skabelonFor(niveau, egne);
    const link = typeof location === 'undefined' ? '' : `${location.origin}${location.pathname}#/mine`;
    const v = rykkerVariabler({ i, r: o.regler, idag, send: nu, booking_nr: x.booking_id, kunde_navn: x.kunde.navn, firma: indst.firma.navn, telefon: indst.firma.telefon, link_betal: link });
    const svar = await this.rpc<{ kanaler: ('mail' | 'sms')[] }>('rykker_send', {
      p_booking: id, p_niveau: niveau, p_kanaler: kanaler, p_emne: udfyld(sk.emne, v), p_mail: udfyld(sk.mail, v), p_sms: udfyld(sk.sms, v),
      p_gebyr: nu.gebyr, p_kompensation: nu.kompensation, p_rente: nu.rente,
    });
    this.c.skift();
    return svar;
  };
  pauseRykker: Api['pauseRykker'] = async (id, til, grund = '') => { await this.rpc('rykker_pause', { p_booking: id, p_til: til, p_grund: grund }); this.c.skift(); };
  rykkerBetalt: Api['rykkerBetalt'] = async (id) => { await this.rpc('rykker_betalt', { p_booking: id }); this.c.skift(); };
  sendTilInkasso: Api['sendTilInkasso'] = async (id, spor = 'collectia', sagsnr = '', fil = null) => {
    if (!gyldigtSagsnr(sagsnr)) throw new ApiFejl('Sagsnummeret må kun have bogstaver, tal og - / . (højst 40 tegn).', { sagsnr: 'Ugyldigt sagsnummer.' });
    await this.rpc('rykker_inkasso', { p_booking: id, p_spor: spor, p_sagsnr: rensSagsnr(sagsnr), p_fil: fil });
    this.c.skift();
  };
  inkassoSagsnr: Api['inkassoSagsnr'] = async (id, sagsnr) => {
    const nr = rensSagsnr(sagsnr);
    if (!nr || !gyldigtSagsnr(nr)) throw new ApiFejl('Skriv sagsnummeret (bogstaver, tal og - / . – højst 40 tegn).', { sagsnr: 'Ugyldigt sagsnummer.' });
    await this.rpc('rykker_inkasso_sagsnr', { p_booking: id, p_sagsnr: nr });
    this.c.skift();
  };
  rykkerBeskeder: Api['rykkerBeskeder'] = (id) => this.rpc('rykker_beskeder', { p_booking: id });
  /** Småsagens PDF-pakke i det private lager "inkasso" under <klient>/<booking>/. */
  gemInkassoFil: Api['gemInkassoFil'] = async (id, pdf) => {
    if (pdf.type !== 'application/pdf' || pdf.size > 10_000_000) throw new ApiFejl('Filen skal være en PDF på højst 10 MB.');
    const sti = `${await this.c.kraevKlient()}/${id}/smaasag-${crypto.randomUUID()}.pdf`;
    const { error } = await this.c.sb.storage.from(LAGER).upload(sti, pdf, { contentType: 'application/pdf', upsert: false });
    if (error) throw new ApiFejl('PDF-pakken kunne ikke gemmes. Tjek forbindelsen, og prøv igen.');
    return sti;
  };
  hentInkassoFil: Api['hentInkassoFil'] = async (sti) => {
    const { data, error } = await this.c.sb.storage.from(LAGER).download(sti);
    if (error || !data) throw new ApiFejl('Filen kunne ikke hentes.');
    return data;
  };
  renteTjek: Api['renteTjek'] = () => this.rpc('rente_tjek_status', {});
  bekraeftRente: Api['bekraeftRente'] = async () => {
    const r = await this.rpc<RenteTjek>('bekraeft_rentesats', {});
    this.c.skift();
    return r;
  };
  gemRykkerRegler: Api['gemRykkerRegler'] = async (r) => {
    const f = tjekRegler(r);
    if (Object.keys(f).length) throw new ApiFejl('Tjek de markerede felter.', f);
    await this.rpc('gem_rykker_regler', { r });
    this.c.skift();
  };
}
